import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_GATEWAY_URL, NOTIFICATIONS_WIDGET_URL, AUTH_WS_URL } from '../../config';
import { gatewayApi } from '../../services/apiService';
import { aggregateByNavSection } from '../../utils/notificationNavMapping';
import { resolveNotificationNavigationTarget } from '../../utils/notificationNavigation';

// The notifications bell and page live in auth-user-services (notifications-widget/) and are loaded
// at runtime as custom elements, so fixes deployed there show up here without a rebuild.

function useWidgetScript() {
  useEffect(() => {
    if (!NOTIFICATIONS_WIDGET_URL || document.querySelector('script[data-notifications-widget]')) return;
    const script = document.createElement('script');
    script.src = NOTIFICATIONS_WIDGET_URL;
    script.async = true;
    script.dataset.notificationsWidget = '';
    document.head.appendChild(script);
  }, []);
}

// Any call through the app's axios instance runs the shared 401 → refresh flow
const refreshToken = () => gatewayApi.get('/auth-service/notifications/unread-count');

function useWidgetElement() {
  const ref = useRef(null);
  useWidgetScript();
  useEffect(() => {
    if (ref.current) ref.current.refreshToken = refreshToken;
  }, []);
  return ref;
}

/** Sidebar row with the unread badge and the dropdown. */
export function NotificationsBellWidget({ collapsed = false }) {
  const ref = useWidgetElement();
  if (!NOTIFICATIONS_WIDGET_URL) return null;
  return (
    <anyemp-notifications-bell
      ref={ref}
      gateway-url={API_GATEWAY_URL}
      ws-url={AUTH_WS_URL}
      collapsed={collapsed ? '' : undefined}
    />
  );
}

/** The /notifications page. */
export function NotificationsPageWidget() {
  const ref = useWidgetElement();
  if (!NOTIFICATIONS_WIDGET_URL) return null;
  return <anyemp-notifications-page ref={ref} gateway-url={API_GATEWAY_URL} ws-url={AUTH_WS_URL} />;
}

/** Links from the widget to this app's pages go through the SPA router (no reload). */
export function useNotificationsNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    const onNavigate = (e) => {
      const target = resolveNotificationNavigationTarget(e.detail?.path, window.location.origin);
      if (!target) return;
      e.preventDefault();
      navigate(target);
    };
    window.addEventListener('anyemp:navigate', onNavigate);
    return () => window.removeEventListener('anyemp:navigate', onNavigate);
  }, [navigate]);
}

/** Unread counters published by the widget → { unreadCount, unreadBySection } for the menu badges. */
export function useNotificationCounts() {
  const [counts, setCounts] = useState({ unreadCount: 0, unreadBySection: {} });
  useEffect(() => {
    const onCounts = (e) => setCounts({
      unreadCount: e.detail?.total ?? 0,
      unreadBySection: aggregateByNavSection(e.detail?.sections ?? []),
    });
    window.addEventListener('anyemp:notifications-counts', onCounts);
    return () => window.removeEventListener('anyemp:notifications-counts', onCounts);
  }, []);
  return counts;
}
